import React, { useState } from 'react';
import axios from 'axios';
import { isNil } from 'lodash';

import { useClaim } from '~/components/ClaimContainer';
import Button from '~/components/core/Atomic/Buttons/Button';
import EmptyState from '~/components/core/EmptyState';
import PermissionsButtonWrapper from '~/components/core/Permissions/PermissionsButtonWrapper';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import StatusChipCore from '~/components/core/StatusChipCore';
import SortableTable from '~/components/core/Tables/SortableTable';
import { useCms } from '~/components/hooks/useCms';
import { DownloadIcon } from '~/components/icons';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import type { DownloadUrlResponse, ScanStateDTO } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';
import { isClaimWriteDisabled } from '~/Utils/ClaimUtils';

import type { DocumentRequestFile } from '../documentRequestDisplay';
import { canAttach, canPreview, canRescan, SCAN_STATE_CHIP } from '../documentRequestDisplay';
import { TEXTS } from '../texts';
import { documentRequestFileDownloadUrl, documentRequestFileRescanUrl } from '../useDocumentRequests';

export type FileRow = DocumentRequestFile & { id: string; document_request_id: number; submitted_at: string | null };

// The Documents table's pagination: the same SortableTable auto-pagination, 25 a page.
const ROWS_PER_PAGE = 25;

interface DocumentRequestFilesTableProps {
  rows: FileRow[];
  showAttachedColumn: boolean;
  emptyText: string;
  onReload: () => Promise<unknown>;
  onAttach: (row: FileRow) => void;
}

const DocumentRequestFilesTable: React.FC<DocumentRequestFilesTableProps> = ({
  rows,
  showAttachedColumn,
  emptyText,
  onReload,
  onAttach,
}) => {
  const { claim } = useClaim({ claimRequired: true });
  const { user } = useCms();
  // The same write gate as the rest of the Documents tab: a view-only role cannot attach or rescan.
  const isWriteDisabled = isClaimWriteDisabled(claim, user, { allowOnClosedClaim: true });
  const [busyFileId, setBusyFileId] = useState<string | null>(null);
  // SortableTable keeps its page when the rows change, so a list that lost a page (the switch turned off, a file
  // attached) could sit on a page past its last. A new key on each lost page remounts it on the first page; a list
  // that gains a page keeps the user where they are. Set while rendering, so the stale page never renders.
  const pageCount = Math.ceil(rows.length / ROWS_PER_PAGE);
  const [paging, setPaging] = useState({ pageCount, tableKey: 0 });
  if (pageCount !== paging.pageCount) {
    setPaging({ pageCount, tableKey: pageCount < paging.pageCount ? paging.tableKey + 1 : paging.tableKey });
  }

  const handlePreview = async (row: FileRow) => {
    // Opened inside the click, before the await: Safari blocks a window.open that follows an await.
    // 'noopener' would make it return null, so the opener is cut by hand instead.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    setBusyFileId(row.id);
    try {
      const { data } = await axios.get<DownloadUrlResponse>(
        documentRequestFileDownloadUrl(claim.organization_id, claim.id, row.document_request_id, row.uploaded_file_id)
      );
      if (tab) tab.location.href = data.url;
      else window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      tab?.close();
      await reportAxiosError(error);
      // As after a refused attach or rescan, reload so the table shows the server's current state.
      await onReload();
    } finally {
      setBusyFileId(null);
    }
  };

  const handleRescan = async (row: FileRow) => {
    setBusyFileId(row.id);
    try {
      await axios.post<ScanStateDTO>(
        documentRequestFileRescanUrl(claim.organization_id, claim.id, row.document_request_id, row.uploaded_file_id)
      );
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      // Success shows the file as Scanning; a refusal shows whatever state the server now holds.
      await onReload();
      setBusyFileId(null);
    }
  };

  const columns = [
    { id: 'name', label: TEXTS.COLUMN_FILE_NAME, disableSort: true },
    {
      id: 'document_request_id',
      label: TEXTS.COLUMN_FROM_LINK,
      disableSort: true,
      specialCell: (row: FileRow) => TEXTS.linkIdDisplay(row.document_request_id),
    },
    {
      id: 'submitted_at',
      label: TEXTS.COLUMN_SUBMITTED,
      specialCell: (row: FileRow) => (isNil(row.submitted_at) ? '-' : serverDateTimeToLocal(row.submitted_at)),
    },
    {
      id: 'scan_state',
      label: TEXTS.COLUMN_SCAN,
      disableSort: true,
      specialCell: (row: FileRow) => (
        <StatusChipCore status={SCAN_STATE_CHIP[row.scan_state].label} color={SCAN_STATE_CHIP[row.scan_state].color} />
      ),
    },
    {
      id: 'attached',
      label: TEXTS.COLUMN_ATTACHED,
      disableSort: true,
      isHidden: !showAttachedColumn,
      specialCell: (row: FileRow) => (isNil(row.attached_document_id) ? TEXTS.ATTACHED_NO : TEXTS.ATTACHED_YES),
    },
    {
      id: 'actions',
      label: '',
      disableSort: true,
      specialCell: (row: FileRow) => (
        <div className="flex gap-8">
          {canPreview(row) && (
            <Button
              color="primary"
              size="small"
              startIcon={<DownloadIcon size={14} iconColor="currentColor" />}
              aria-label={TEXTS.PREVIEW_ARIA_LABEL(row.name)}
              disabled={busyFileId === row.id}
              onClick={() => handlePreview(row)}
            >
              {TEXTS.PREVIEW_BUTTON}
            </Button>
          )}
          {canAttach(row) && (
            <PermissionsButtonWrapper action={PERMISSION_ACTIONS.CLAIM_DOCUMENT} verb={PERMISSION_VERBS.WRITE}>
              <Button
                color="primary"
                size="small"
                aria-label={TEXTS.ATTACH_ARIA_LABEL(row.name)}
                disabled={isWriteDisabled || busyFileId === row.id}
                onClick={() => onAttach(row)}
              >
                {TEXTS.ATTACH_BUTTON}
              </Button>
            </PermissionsButtonWrapper>
          )}
          {canRescan(row) && (
            <PermissionsButtonWrapper action={PERMISSION_ACTIONS.CLAIM_DOCUMENT} verb={PERMISSION_VERBS.WRITE}>
              <Button
                color="primary"
                size="small"
                aria-label={TEXTS.RESCAN_ARIA_LABEL(row.name)}
                disabled={isWriteDisabled || busyFileId === row.id}
                onClick={() => handleRescan(row)}
              >
                {TEXTS.RESCAN_BUTTON}
              </Button>
            </PermissionsButtonWrapper>
          )}
        </div>
      ),
    },
  ];

  return (
    <SortableTable
      key={paging.tableKey}
      rows={rows}
      autoPaginateRowsPerPage={ROWS_PER_PAGE}
      columns={columns}
      keepRowsOrder
      disableSortByUser
      emptyStateComponent={<EmptyState subtitle={emptyText} illustration={<></>} />}
    />
  );
};

export default DocumentRequestFilesTable;
